import { useTranslation } from "react-i18next";
import { AppShell } from "../components/AppShell";
import { Card, ErrorBox, Skeleton } from "../components/ui";
import { useAiMetrics, useDemoMetrics } from "../lib/api";
import { fmtNumber, fmtPct } from "../lib/format";

/** Demo metrics (requirement 9.10): time saved, manual steps removed, exceptions found, decision-cycle reduction and AI
 * acceptance — counted from the records and the AI interaction log, with every assumption shown next to the figure. */
export default function DemoMetrics() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = useDemoMetrics();
  const ai = useAiMetrics();
  const d = q.data;
  const trackName = (track: string) => t(`demo.tracks.${track}`, track);
  return (
    <AppShell subtitle={t("demo.title")}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-brand-primary">{t("demo.title")}</h1>
          <p className="mt-1 text-sm text-brand-grey">{t("demo.subtitle")}</p>
        </div>
      </div>
      {q.isError && <div className="mt-4"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>}
      {q.isLoading && <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>}
      {d && (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-demo-tiles>
            <Tile label={t("demo.timeSaved")} value={`${fmtNumber(d.time_saved.total_hours, lng, 1)} ${t("demo.hours")}`} hint={`${fmtNumber(d.time_saved.total_steps, lng, 0)} ${t("demo.steps")}`} />
            <Tile label={t("demo.stepsRemoved")} value={fmtNumber(d.time_saved.total_steps, lng, 0)} hint={t("demo.breakdown")} />
            <Tile label={t("demo.exceptionsFound")} value={fmtNumber(d.exceptions.total, lng, 0)} hint={`${fmtNumber(d.exceptions.open, lng, 0)} ${t("demo.open")}`} />
            <Tile label={t("demo.cycle")}
              value={d.decision_cycle.median_hours == null ? "–" : `${fmtNumber(d.decision_cycle.median_hours / 24, lng, 1)} ${t("demo.days")}`}
              hint={d.decision_cycle.median_hours == null ? t("demo.noCycle") : `${fmtPct(d.decision_cycle.reduction_pct, lng, 0, false)} ${t("demo.reduction")} · ${t("demo.baseline")} ${fmtNumber(d.decision_cycle.baseline_hours / 24, lng, 0)} ${t("demo.days")}`} />
          </div>
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Card className="p-4">
              <h2 className="font-heading text-sm font-semibold">{t("demo.breakdown")}</h2>
              <table className="mt-2 w-full text-xs">
                <thead className="text-left text-[11px] uppercase tracking-wide text-brand-grey"><tr><th className="py-1">{t("demo.step")}</th><th>{t("demo.track")}</th><th className="text-right">{t("demo.count")}</th><th className="text-right">{t("demo.minutes")}</th><th className="text-right">{t("demo.saved")} ({t("demo.hours")})</th></tr></thead>
                <tbody>
                  {d.time_saved.rows.map((r) => (
                    <tr key={r.step} className="border-t border-brand-grey-light/70">
                      <td className="py-1">{t(`demo.stepNames.${r.step}`, r.step)}</td><td className="text-brand-grey">{trackName(r.track)}</td>
                      <td className="text-right tabular-nums">{fmtNumber(r.count, lng, 0)}</td><td className="text-right tabular-nums text-brand-grey">{r.minutes}</td><td className="text-right tabular-nums font-semibold">{fmtNumber(r.hours, lng, 1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
            <Card className="p-4">
              <h2 className="font-heading text-sm font-semibold">{t("demo.kinds")}</h2>
              <table className="mt-2 w-full text-xs">
                <thead className="text-left text-[11px] uppercase tracking-wide text-brand-grey"><tr><th className="py-1">{t("demo.kind")}</th><th>{t("demo.track")}</th><th className="text-right">{t("demo.total")}</th><th className="text-right">{t("demo.open")}</th></tr></thead>
                <tbody>
                  {d.exceptions.rows.map((r) => (
                    <tr key={r.kind} className="border-t border-brand-grey-light/70">
                      <td className="py-1">{t(`demo.kindNames.${r.kind}`, r.kind)}</td><td className="text-brand-grey">{trackName(r.track)}</td>
                      <td className="text-right tabular-nums font-semibold">{fmtNumber(r.total, lng, 0)}</td><td className="text-right tabular-nums">{fmtNumber(r.open, lng, 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <h2 className="mt-5 font-heading text-sm font-semibold">{t("demo.acceptance")}</h2>
              {ai.isLoading && <Skeleton className="mt-2 h-16" />}
              {ai.isError && <p className="mt-2 text-xs text-brand-grey">{t("demo.noAi")}</p>}
              {ai.data && (
                <div className="mt-2 text-xs" data-ai-metrics>
                  <div className="flex flex-wrap gap-4">
                    <span><span className="font-heading text-lg font-semibold">{fmtNumber(ai.data.total, lng, 0)}</span> {t("demo.interactions")}</span>
                    <span><span className="font-heading text-lg font-semibold">{fmtNumber(ai.data.rated, lng, 0)}</span> {t("demo.rated")}</span>
                    {(["useful", "correct", "needs_review", "not_useful"] as const).map((f) => (
                      <span key={f} className="rounded border border-brand-grey-light px-2 py-0.5">{t(`feedback.${f}`)} <span className="font-semibold">{fmtNumber(ai.data!.by_feedback[f] ?? 0, lng, 0)}</span></span>
                    ))}
                  </div>
                  {ai.data.by_kind.length > 0 && (
                    <table className="mt-2 w-full">
                      <thead className="text-left text-[11px] uppercase tracking-wide text-brand-grey"><tr><th className="py-1">{t("demo.byKind")}</th><th className="text-right">{t("demo.interactions")}</th><th className="text-right">{t("demo.rated")}</th><th className="text-right">{t("feedback.useful")} / {t("feedback.correct")}</th><th className="text-right">{t("feedback.needs_review")} / {t("feedback.not_useful")}</th></tr></thead>
                      <tbody>
                        {ai.data.by_kind.map((k) => (
                          <tr key={k.kind} className="border-t border-brand-grey-light/70"><td className="py-1">{k.kind}</td><td className="text-right tabular-nums">{k.total}</td><td className="text-right tabular-nums">{k.rated}</td><td className="text-right tabular-nums text-status-green">{k.useful + k.correct}</td><td className="text-right tabular-nums text-status-amber">{k.needs_review + k.not_useful}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </Card>
          </div>
          <Card className="mt-4 p-4 text-xs text-brand-grey">
            <h2 className="font-heading text-sm font-semibold text-brand-charcoal">{t("demo.assumptions")}</h2>
            <p className="mt-1">{t("demo.assumptionsHint")}</p>
            <p className="mt-1">{t("demo.cycleHint")} · {t("demo.baseline")}: {fmtNumber(d.assumptions.manual_decision_hours, lng, 0)} {t("demo.hours")}</p>
            <p className="mt-2 rounded bg-[#F6F8FA] px-3 py-2 text-brand-charcoal">{t("demo.writeBack")}</p>
            {d.since && <p className="mt-2">{t("demo.since")} {d.since.slice(0, 10)} · {d.as_of.slice(0, 16)}</p>}
          </Card>
        </>
      )}
    </AppShell>
  );
}

function Tile({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <Card className="p-4">
      <div className="text-[11px] uppercase tracking-wide text-brand-grey">{label}</div>
      <div className="mt-1 font-heading text-2xl font-semibold text-brand-primary">{value}</div>
      <div className="mt-1 text-xs text-brand-grey">{hint}</div>
    </Card>
  );
}
